'use client';

import type { NextPage } from 'next';
import styles from '../../styles/home/Home.module.scss';
import Header from 'components/header';
import UsersScrollComponent from 'components/usersScrollComponent';
import EventScrollComponent from 'components/eventsScroll';
import Button from 'componentsUnits/button';
import { getLatestEvents } from 'actions/event';
import { getLatestProfiles, getMyProfile } from 'actions/profile';
import Break from 'componentsUnits/break';
import { useEffect, useState } from 'react';
import { getMyActivity } from 'actions/activity';
import { updateToken, resetUser } from 'actions/user';
import { getLatestPosts, getLatestPostsByUsers } from 'actions/post';
import PostScrollComponent from 'components/postScrollComponent';
import { setLastHomeRefresh } from 'actions/general';
import { runIfLastRefreshWasMinutesAgo, sortEntriesDateIndex } from 'helpers/general';
import RowComponent from 'componentsUnits/row';
import CommunitiesScrollComponent from 'components/communitiesScrollComponent';
import { Size } from 'types/size';
import { getLatestCommunities } from 'actions/community';
import { PostForm } from 'components/postForm';
import Row from 'componentsUnits/row';
import EntryScrollHorizontalComponent from 'components/entryScroll';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useAppDispatch, useAppSelector } from 'reducers/storeHooks';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { PublicProfile } from 'types/publicProfile';
import Subtitle from 'componentsUnits/subtitle';
import { Type } from 'types/type';
import ColumnComponent from 'componentsUnits/columnComponent';
import { isDev } from '../general';
import { mixpanelActions } from 'helpers/analytics';
import { useRouter } from 'next/navigation';
import Loading from '../loading';
import ButtonText from 'componentsUnits/buttonText';
import { localeTexts } from 'locale/texts';
import CategoryEventScrollComponent from 'components/categoryEventsScrollComponent';
import { Interest } from 'types/interest';

const Home: NextPage = () => {
    const uid = useAppSelector((state) => state.userReducer?.user?.uid);
    const { entryDateIndex: entryDateIndexCommunity, loading: communitiesLoading } = useAppSelector((state) => state.communityReducer);
    const { entryDateIndex: entryDateIndexEvent, loading: eventsLoading } = useAppSelector((state) => state.eventReducer);
    const events = useAppSelector((state) => state.eventReducer.events);
    const activity = useAppSelector((state) => state.activityReducer);
    const publicProfiles = useAppSelector((state) => state.profileReducer.publicProfiles);
    const [latestProfiles, setLatestProfiles] = useState<PublicProfile[]>([]);
    const tokenLoading = useAppSelector((state) => state.userReducer.tokenLoading);
    const [lastLoadMore, setLastLoadMore] = useState(0);
    const router = useRouter();
    // const interests = useAppSelector((state) => state.interestReducer.interests)

    const publicProfilesSize = publicProfiles instanceof Map ? publicProfiles.size : 0;

    useEffect(() => {
        if (!uid || !(publicProfiles instanceof Map)) return;

        const temp: PublicProfile[] = [];
        publicProfiles.forEach((value, key) => {
            if (key !== uid && !activity.friendList[key] && !activity.followedUsers.includes(key)) {
                temp.push(value);
            }
        });
        setLatestProfiles(temp);
    }, [publicProfiles, publicProfilesSize, uid, activity]);

    const dispatch = useAppDispatch();
    const [activeInterests, setActiveInterests] = useState<string[]>([]);
    const [isFirstRefreshFinished, setIsFirstRefreshFinished] = useState(false);
    const { entryDateIndex: entryDateIndexPosts, loading: postsLoading } = useAppSelector((state) => state.postReducer);
    const { profile: myProfile, loading: myProfileLoading } = useAppSelector((state) => state.profileReducer);
    const lastHomeRefresh = useAppSelector((state) => state.mainReducer.lastHomeRefresh);

    const refresh = async (force?: boolean) => {
        try {
            await dispatch(updateToken(true));
            if (!myProfileLoading && !myProfile) {
                isDev && console.log("HOME: GET MY PROFILE");
                await dispatch(getMyProfile());
                return;
            }

            if (!force && new Date().getTime() - lastHomeRefresh < 60000) {
                return;
            }

            dispatch(getLatestPosts("", myProfile?.country, myProfile?.city, true))
            dispatch(getLatestPostsByUsers(activity.followedUsers, "", true))
            dispatch(getLatestCommunities(myProfile?.country, myProfile?.city, true))
            dispatch(getLatestProfiles(myProfile?.country, myProfile?.city, 20, true))
            dispatch(getLatestEvents(myProfile?.country, myProfile?.city, "", 30, true))
            dispatch(getMyActivity())

            dispatch(setLastHomeRefresh(new Date().getTime()));
            setIsFirstRefreshFinished(true);
        } catch (error) {
            isDev && console.error("Error in refresh:", error);
        }
    };

    const hasNoContent = () => {
        return !postsLoading && !communitiesLoading && !eventsLoading &&
            events.size === 0 && entryDateIndexCommunity.length === 0 && entryDateIndexPosts.length === 0 && entryDateIndexEvent.length === 0;
    };

    const loadMore = () => {
        const currentTime = new Date().getTime();
        if (currentTime - lastLoadMore < 3000) return;

        const lastPostDate = entryDateIndexPosts[entryDateIndexPosts.length - 1]?.date;
        const lastEventDate = entryDateIndexEvent[entryDateIndexEvent.length - 1]?.date;

        if (lastPostDate) {
            dispatch(getLatestPosts(lastPostDate.toString(), myProfile?.country, myProfile?.city, false));
        }
        if (lastEventDate) {
            dispatch(getLatestEvents(myProfile?.country, myProfile?.city, lastEventDate.toString(), 30, false));
        }
        setLastLoadMore(currentTime);
    };

    useEffect(() => {
        if (isFirstRefreshFinished || tokenLoading || !myProfile) return;
        setActiveInterests(myProfile.interests || []);
        runIfLastRefreshWasMinutesAgo(10, lastHomeRefresh, refresh);
    }, [myProfile, tokenLoading, isFirstRefreshFinished, lastHomeRefresh]);

    useEffect(() => {
        mixpanelActions.track('Home Page: visited', {
            public_profiles_length: publicProfilesSize,
            events_length: events.size,
            posts_length: entryDateIndexPosts.length,
            communities_length: entryDateIndexCommunity.length,
        })
    }, [])

    if (!uid) {
        return (
            <Loading>
                <RowComponent center fullWidth>
                    <Subtitle text={localeTexts.errorPageMessage} type={Type.dark} size={Size.small} />
                    <Break />
                    <ButtonText text='Reload' onClick={() => { refresh(true); }} />
                </RowComponent>
            </Loading>
        );
    }

    // const getInterestsFromEvents = () => {
    //     const interests: Interest[] = [];
    //     events.forEach((event) => {
    //         event.interests?.forEach((interest) => {
    //             if (interest !== "" && !interests.some((i) => i.title === interest)) {
    //                 interests.push({ title: interest });
    //             }
    //         });
    //     });
    //     return interests;
    // }


    return (
        <div className={`${styles.home_container}`}>
            <div className={styles.container}>
                <Header text={localeTexts.appTitle} disableBack />
                <PullToRefresh onRefresh={() => refresh(true)} pullingContent={""}>
                    <>
                        <Break />
                        <PostForm />
                        {/* <Break />

                        <CategoryEventScrollComponent
                            title={'Styles'}
                            activeInterests={activeInterests}
                            setActiveInterests={setActiveInterests}
                            // customInterests={getInterestsFromEvents()}
                        /> */}

                        <Break />

                        {hasNoContent() &&
                            <ColumnComponent centerH>
                                <Subtitle text={localeTexts.event.noEventsYetCreate} type={Type.dark} size={Size.xSmall} />
                                <Break />
                                <Break />
                                <Break />
                                <Break />
                                <Row gap>
                                    <Button text={localeTexts.event.create} type={"12"} size={Size.xSmall} onClick={() => { router.push("/event/create") }} />
                                </Row>
                                <Break size={Size.small} />
                                <Button type='10' size={Size.xxxSmall} text={localeTexts.refresh} onClick={refresh} />
                            </ColumnComponent>
                        }

                        {events.size > 0 &&
                            <EntryScrollHorizontalComponent
                                activeInterests={activeInterests}
                                showAll={activeInterests.length === 0}
                                entryDateIndex={entryDateIndexEvent?.slice(0, 10)}
                                title={'Latest Events'}
                                activity={activity}
                            />
                        }
                        <Break size={Size.xxLarge} />

                        {eventsLoading && entryDateIndexEvent?.length === 0 && <><Skeleton height={200} /><Break /><Skeleton height={200} /><Break /></>}
                        {postsLoading && entryDateIndexPosts?.length === 0 && <><Skeleton height={200} /><Break /><Skeleton height={200} /><Break /></>}
                        {communitiesLoading && entryDateIndexCommunity?.length === 0 && <><Skeleton height={200} /><Break /><Skeleton height={200} /><Break /></>}

                        <PostScrollComponent entryDateIndex={sortEntriesDateIndex(entryDateIndexPosts).slice(0, 8)} title="" />
                        <Break />

                        {latestProfiles && latestProfiles.length > 0 && (
                            <>
                                <UsersScrollComponent title={localeTexts.profile.peopleYouMightKnow} profiles={latestProfiles} />
                                <Break />
                            </>
                        )}

                        <EventScrollComponent title={''} entryDateIndex={entryDateIndexEvent?.slice(0, 3)} activity={activity} activeInterests={activeInterests} showAll={activeInterests.length === 0} />
                        <Break />

                        <CommunitiesScrollComponent title={''} entryDateIndex={entryDateIndexCommunity?.slice(0, 2)} activity={activity} activeInterests={activeInterests} showAll={activeInterests.length === 0} />
                        <Break />

                        <EventScrollComponent title={''} entryDateIndex={entryDateIndexEvent?.slice(3)} activity={activity} activeInterests={activeInterests} showAll={activeInterests.length === 0} />
                        <CommunitiesScrollComponent title={''} entryDateIndex={entryDateIndexCommunity?.slice(3)} activity={activity} activeInterests={activeInterests} showAll={activeInterests.length === 0} />
                    </>
                </PullToRefresh>
            </div>
            <Break />

            {!hasNoContent() &&
                <ButtonText text={localeTexts.loadMore} onClick={loadMore} />
            }
            <Break />
            <Break />
        </div>
    );
}

export default Home;
